import { BellRing, BellOff } from "lucide-react";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format";

/**
 * M23d — enrolling this device for web push.
 *
 * Everything here is about not lying to Andy about the state of a permission that lives in
 * the browser rather than in the app. Four states are genuinely different and each gets its
 * own words: the browser cannot do push at all, the server has no keys, permission was
 * refused (which the app cannot re-ask for), and subscribed.
 *
 * M26c — the browser machinery (support check, subscription probe, subscribe/unsubscribe)
 * moved verbatim into `usePushSubscription`, shared with the enable-push banner on the
 * notifications page. This component is the four states' words; the hook is the plumbing.
 */
export function PushDevices() {
  const { supported, checked, denied, endpoint, config, subscribe, unsubscribe } = usePushSubscription();

  if (config.isLoading || !checked) return <Skeleton className="h-24 w-full" />;

  const others = (config.data?.devices ?? []).length - (endpoint ? 1 : 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>This device</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!supported ? (
          <p className="text-xs text-muted-foreground">
            This browser cannot receive web push. On iOS it works only once the app is added to
            the home screen.
          </p>
        ) : config.data?.configured !== true ? (
          // Named precisely, because the fix is a command on the box rather than anything
          // Andy can do from here.
          <p className="text-xs text-muted-foreground">
            Push is not set up on the server — no VAPID keys. Run{" "}
            <code className="rounded bg-muted px-1">scripts/generate-vapid.mjs</code> on the box
            and restart the app.
          </p>
        ) : denied && endpoint === null ? (
          <p className="text-xs text-muted-foreground">
            This browser has blocked notifications for the site. The app cannot ask again — turn
            them back on in the browser's own site settings.
          </p>
        ) : endpoint === null ? (
          <>
            <p className="text-xs text-muted-foreground">
              Not subscribed. Nothing in the Push column below can reach you until it is.
            </p>
            <Button
              variant="outline"
              className="w-full"
              disabled={subscribe.isPending}
              onClick={() => subscribe.mutate()}
            >
              <BellRing className="h-4 w-4" /> Notify this device
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">subscribed</Badge>
              {others > 0 && (
                <span className="text-xs text-muted-foreground">
                  {others} other {others === 1 ? "device" : "devices"} also subscribed
                </span>
              )}
            </div>
            <Button
              variant="outline"
              className="w-full"
              disabled={unsubscribe.isPending}
              onClick={() => unsubscribe.mutate()}
            >
              <BellOff className="h-4 w-4" /> Stop notifying this device
            </Button>
          </>
        )}

        {(config.data?.devices.length ?? 0) > 0 && (
          <ul className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
            {config.data?.devices.map((device) => (
              <li key={device.id} className="flex items-center justify-between gap-2">
                <span className="truncate">{shortAgent(device.userAgent)}</span>
                <span className="shrink-0">{formatDateTime(device.lastSeenAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** A user-agent string is unreadable on a phone; the platform is the part that identifies it. */
function shortAgent(userAgent: string | null): string {
  if (!userAgent) return "Unknown device";
  const platform = /\(([^)]+)\)/.exec(userAgent)?.[1];
  return platform ? platform.split(";")[0]!.trim() : userAgent.slice(0, 40);
}
